import { useEffect, useRef, useState } from 'react';
import { Scissors, Type, X, Wand2 } from 'lucide-react';
import { convertToH264Mp4 } from '@/lib/ffmpegConvert';

/**
 * Минимальный видео-редактор сторис:
 *   - Trim: два handle'а [start, end]
 *   - Скорость 0.5×/1×/1.5×/2×
 *   - Текст поверх кадра (x,y,color)
 *
 * Архитектура рендера:
 *   - Preview: показываем сам <video>, текст-overlay поверх абсолютным <div>.
 *   - Export: поднимаем скрытый canvas, раз в кадр рисуем туда
 *     drawImage(video) + drawText, MediaRecorder захватывает canvas.captureStream().
 *   Разделение критично — раньше я пытался рисовать canvas всё время с
 *   hidden-video, но `opacity:0`/`display:none` видео Chrome паузит и
 *   `videoWidth` остаётся 0.  Сейчас видео играет нормально, canvas
 *   включается только в момент записи.
 */

type Props = {
  srcFile: File;
  onCancel: () => void;
  onDone: (file: File) => void;
};

const SPEEDS = [0.5, 1, 1.5, 2] as const;

export default function VideoTrimmer({ srcFile: initialFile, onCancel, onDone }: Props) {
  // srcFile может меняться на лету (после конвертации заменяем file).
  const [srcFile, setSrcFile] = useState<File>(initialFile);
  useEffect(() => setSrcFile(initialFile), [initialFile]);

  const [isConverting, setIsConverting] = useState(false);
  const [convertProgress, setConvertProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // ObjectURL пересоздаём при смене srcFile (например, после конвертации).
  // Lazy useState даёт URL уже на первом рендере, а не через useEffect,
  // иначе <video> стреляет onError пока src пустой.
  const [previewUrl, setPreviewUrl] = useState<string>(() => URL.createObjectURL(srcFile));
  useEffect(() => {
    const url = URL.createObjectURL(srcFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [srcFile]);

  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);

  const [overlayText, setOverlayText] = useState('');
  const [textX, setTextX] = useState(50); // %
  const [textY, setTextY] = useState(85);
  const [textColor, setTextColor] = useState('#ffffff');

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Проверка браузерной поддержки формата: если canPlayType возвращает ''
  // для mime-type исходного файла, редактор технически не сможет открыть
  // декодер.  Показываем юзеру явное сообщение и предлагаем пропустить.
  const [unsupportedFormat, setUnsupportedFormat] = useState(false);
  useEffect(() => {
    if (!srcFile.type) return;
    const probe = document.createElement('video');
    const support = probe.canPlayType(srcFile.type);
    if (!support) setUnsupportedFormat(true);
  }, [srcFile]);

  const onMetadata = () => {
    const v = videoRef.current;
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
    setDuration(v.duration);
    setTrimEnd(Math.min(v.duration, 60));
  };

  // Страховка: некоторые .webm/.mkv отдают duration позже чем loadedmetadata.
  useEffect(() => {
    if (duration > 0) return;
    const iv = setInterval(() => {
      const v = videoRef.current;
      if (v && Number.isFinite(v.duration) && v.duration > 0) {
        setDuration(v.duration);
        setTrimEnd(Math.min(v.duration, 60));
        clearInterval(iv);
      }
    }, 250);
    return () => clearInterval(iv);
  }, [duration]);

  // Держим currentTime в границах trim при live-play.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || duration === 0) return;
    const tick = () => {
      if (v.currentTime < trimStart) v.currentTime = trimStart;
      if (v.currentTime >= trimEnd) {
        v.currentTime = trimStart;
        v.play().catch(() => {});
      }
    };
    v.addEventListener('timeupdate', tick);
    return () => v.removeEventListener('timeupdate', tick);
  }, [trimStart, trimEnd, duration]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = speed;
  }, [speed]);

  const doExport = async () => {
    setError(null);
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) { setError('Элементы редактора не готовы'); return; }
    if (!v.videoWidth || !v.videoHeight) {
      setError('Видео ещё не загружено. Подожди секунду и нажми снова.');
      return;
    }
    setIsExporting(true);
    setExportProgress(0);

    try {
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      const ctx = c.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D недоступен');

      // Подготовка audio: video должен быть не muted, чтобы в captureStream
      // взвёлся audio track.  Громкость на 0 — чтоб юзер не слышал.
      v.pause();
      v.muted = false;
      v.volume = 0;

      const canvasStream = c.captureStream(30);

      const captureFn = (v as HTMLVideoElement & {
        captureStream?: () => MediaStream;
        mozCaptureStream?: () => MediaStream;
      });
      const videoSrcStream =
        captureFn.captureStream?.() ?? captureFn.mozCaptureStream?.();
      if (videoSrcStream) {
        for (const track of videoSrcStream.getAudioTracks()) {
          canvasStream.addTrack(track);
        }
      }

      const mimeCandidates = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4;codecs=avc1,mp4a',
        'video/mp4',
      ];
      const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m));
      if (!mime) throw new Error('Браузер не поддерживает MediaRecorder');

      const chunks: Blob[] = [];
      const recorder = new MediaRecorder(canvasStream, {
        mimeType: mime,
        videoBitsPerSecond: 4_000_000,
      });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      const stopPromise = new Promise<Blob>((resolve) => {
        recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      });

      // Цикл отрисовки: drawImage(video) + drawText.
      let rafId = 0;
      const drawLoop = () => {
        ctx.drawImage(v, 0, 0, c.width, c.height);
        if (overlayText) {
          drawOverlay(ctx, c.width, c.height, overlayText, textX, textY, textColor);
        }
        rafId = requestAnimationFrame(drawLoop);
      };

      v.currentTime = trimStart;
      v.playbackRate = speed;
      await new Promise((r) => setTimeout(r, 80));

      recorder.start(100);
      rafId = requestAnimationFrame(drawLoop);
      await v.play();

      const effectiveLen = (trimEnd - trimStart) / speed;
      const startedAt = performance.now();
      await new Promise<void>((resolve) => {
        const watch = () => {
          const elapsed = (performance.now() - startedAt) / 1000;
          setExportProgress(Math.min(100, Math.round((elapsed / effectiveLen) * 100)));
          if (v.currentTime >= trimEnd || elapsed >= effectiveLen + 0.5) {
            resolve();
          } else {
            requestAnimationFrame(watch);
          }
        };
        requestAnimationFrame(watch);
      });

      cancelAnimationFrame(rafId);
      v.pause();
      v.muted = true; // вернём как было для preview loop
      v.volume = 1;
      recorder.stop();

      const blob = await stopPromise;
      const ext = mime.includes('mp4') ? 'mp4' : 'webm';
      const file = new File(
        [blob],
        srcFile.name.replace(/\.[^.]+$/, `-edited.${ext}`),
        { type: mime },
      );
      onDone(file);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Ошибка экспорта';
      setError(msg);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-surface-800">
        <h3 className="text-white font-semibold">Редактор видео</h3>
        <button onClick={onCancel} className="text-white"><X size={20} /></button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3">
        {/* Preview — видео видимо, canvas скрыт и включается только при экспорте */}
        <div className="relative max-h-[45vh] flex justify-center bg-black rounded-xl overflow-hidden">
          <video
            ref={videoRef}
            src={previewUrl}
            onLoadedMetadata={onMetadata}
            onLoadedData={onMetadata}
            onError={(e) => {
              const v = e.currentTarget;
              const code = v.error?.code;
              const map: Record<number, string> = {
                1: 'загрузка прервана (MEDIA_ERR_ABORTED)',
                2: 'сетевая ошибка (MEDIA_ERR_NETWORK)',
                3: 'ошибка декодера (MEDIA_ERR_DECODE)',
                4: 'формат/кодек не поддерживается браузером (MEDIA_ERR_SRC_NOT_SUPPORTED)',
              };
              const why = code ? map[code] ?? `код ${code}` : 'неизвестная ошибка';
              setError(
                `Не удалось открыть видео: ${why}. ` +
                `Тип файла: ${srcFile.type || 'не определён'}. ` +
                (code === 4
                  ? 'Редактор требует MP4 (H.264) / WebM / .mov с кодеком H.264. iPhone-видео в HEVC в Chrome на Windows не откроется — загрузи без редактирования или перекодируй в H.264.'
                  : '')
              );
            }}
            autoPlay
            muted
            loop
            playsInline
            className="max-h-[45vh] max-w-full"
          />
          {/* Live-preview overlay текста поверх видео */}
          {overlayText && (
            <div
              className="pointer-events-none absolute font-bold"
              style={{
                left: `${textX}%`,
                top: `${textY}%`,
                transform: 'translate(-50%, -50%)',
                color: textColor,
                fontSize: '7vw',
                lineHeight: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.9)',
                fontFamily: '"Quicksand","Nunito",system-ui,sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              {overlayText}
            </div>
          )}
          {/* Canvas для записи — off-screen.  Никогда не отображается пользователю. */}
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', left: -99999, top: -99999, pointerEvents: 'none' }}
          />
        </div>

        {/* Trim */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-surface-300">
            <Scissors size={14} /> Обрезка {trimStart.toFixed(1)}–{trimEnd.toFixed(1)}s
            {duration > 0 && (
              <span className="text-surface-500">
                · итог {((trimEnd - trimStart) / speed).toFixed(1)}s из {duration.toFixed(1)}s
              </span>
            )}
          </div>
          <div className="relative h-10 bg-surface-800 rounded-lg">
            <input
              type="range"
              min={0}
              max={Math.max(duration, 0.1)}
              step={0.1}
              value={trimStart}
              disabled={duration === 0}
              onChange={(e) => setTrimStart(Math.min(parseFloat(e.target.value), Math.max(0, trimEnd - 0.5)))}
              className="absolute inset-x-0 top-1 w-full"
            />
            <input
              type="range"
              min={0}
              max={Math.max(duration, 0.1)}
              step={0.1}
              value={trimEnd}
              disabled={duration === 0}
              onChange={(e) => setTrimEnd(Math.max(parseFloat(e.target.value), trimStart + 0.5))}
              className="absolute inset-x-0 bottom-1 w-full"
            />
          </div>
        </div>

        {/* Speed */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-300">Скорость</span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              className={`px-3 py-1 rounded-full text-xs ${speed === s ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-300'}`}
            >
              {s}×
            </button>
          ))}
        </div>

        {/* Text overlay */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-surface-300">
            <Type size={14} /> Текст поверх
          </div>
          <input
            value={overlayText}
            onChange={(e) => setOverlayText(e.target.value)}
            placeholder="Надпись"
            maxLength={80}
            className="w-full bg-surface-800 rounded-lg px-3 py-2 text-sm text-white"
          />
          {overlayText && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              <label className="space-y-1">
                <span className="text-surface-400">X%</span>
                <input type="range" min={0} max={100} value={textX} onChange={(e) => setTextX(+e.target.value)} className="w-full" />
              </label>
              <label className="space-y-1">
                <span className="text-surface-400">Y%</span>
                <input type="range" min={0} max={100} value={textY} onChange={(e) => setTextY(+e.target.value)} className="w-full" />
              </label>
              <label className="space-y-1">
                <span className="text-surface-400">Цвет</span>
                <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-full h-7 bg-transparent" />
              </label>
            </div>
          )}
        </div>

        {(error || unsupportedFormat) && !isConverting && (
          <div className="bg-red-950/40 border border-red-900 rounded-lg p-3 space-y-2">
            {error && <p className="text-red-300 text-xs">{error}</p>}
            {unsupportedFormat && !error && (
              <p className="text-red-300 text-xs">
                Браузер не поддерживает формат «{srcFile.type || 'unknown'}».
                Обычно это HEVC (.mov или .mp4 с iPhone/нового Android).
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  setError(null);
                  setIsConverting(true);
                  setConvertProgress(0);
                  try {
                    const converted = await convertToH264Mp4(srcFile, setConvertProgress);
                    setSrcFile(converted);
                    setDuration(0); // форсим перечитать метаданные
                    setTrimStart(0);
                    setTrimEnd(0);
                  } catch (e) {
                    setError(
                      'Конвертация не удалась: ' +
                      (e instanceof Error ? e.message : String(e)),
                    );
                  } finally {
                    setIsConverting(false);
                  }
                }}
                className="text-xs bg-brand-600 hover:bg-brand-500 text-white px-3 py-1.5 rounded inline-flex items-center gap-1"
              >
                <Wand2 size={12} /> Конвертировать в MP4 (H.264)
              </button>
              <button
                onClick={() => onDone(srcFile)}
                className="text-xs bg-red-900/60 hover:bg-red-900 text-white px-3 py-1.5 rounded"
              >
                Пропустить редактор
              </button>
            </div>
            <p className="text-[10px] text-surface-500">
              Конвертация идёт в браузере через ffmpeg.wasm (~30 МБ загрузки на первое использование).
              Для 30-сек ролика занимает 15–60 сек.
            </p>
          </div>
        )}

        {isConverting && (
          <div className="bg-surface-800 rounded-lg p-3 space-y-1">
            <div className="text-xs text-surface-300 flex items-center gap-2">
              <Wand2 size={12} /> Конвертация в H.264… {Math.round(convertProgress * 100)}%
            </div>
            <div className="w-full h-1.5 bg-surface-900 rounded-full overflow-hidden">
              <div className="h-full bg-brand-500 transition-all" style={{ width: `${Math.round(convertProgress * 100)}%` }} />
            </div>
          </div>
        )}
        {isExporting && (
          <div className="space-y-1">
            <div className="text-xs text-surface-300">Экспорт… {exportProgress}%</div>
            <div className="w-full h-1.5 bg-surface-800 rounded-full overflow-hidden">
              <div className="h-full bg-brand-500 transition-all" style={{ width: `${exportProgress}%` }} />
            </div>
            <p className="text-[10px] text-surface-500">Запись идёт в реальном времени — не закрывай вкладку</p>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-surface-800 flex gap-2">
        <button
          onClick={onCancel}
          disabled={isExporting}
          className="px-4 py-2 bg-surface-800 text-surface-200 rounded-lg text-sm disabled:opacity-40"
        >
          Отмена
        </button>
        <button
          onClick={doExport}
          disabled={isExporting || duration === 0}
          className="flex-1 py-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 rounded-lg text-white text-sm font-medium"
        >
          {isExporting ? 'Экспорт…' : 'Готово'}
        </button>
      </div>
    </div>
  );
}

function drawOverlay(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  text: string, xPct: number, yPct: number, color: string,
) {
  const fontSize = Math.round(H * 0.055);
  ctx.font = `bold ${fontSize}px "Quicksand","Nunito",system-ui,sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const x = (W * xPct) / 100;
  const y = (H * yPct) / 100;
  ctx.lineWidth = Math.max(2, fontSize * 0.08);
  ctx.strokeStyle = 'rgba(0,0,0,0.75)';
  ctx.fillStyle = color;
  ctx.strokeText(text, x, y);
  ctx.fillText(text, x, y);
}
